import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`As console logs can contain sensitive information, we do not capture these logs automatically.
You can enable this feature from your client-side by setting `}<inlineCode parentName="p">{`captureLogs = true`}</inlineCode>{` in our `}<a parentName="p" {...{
        "href": "/docs/session-replay/installation?tab=iOS#step-two-enable-session-recordings-in-your-project-settings"
      }}>{`iOS SDK config`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ios_swift"
      }}>{`let config = PostHogConfig(apiKey: "<ph_project_api_key>", host: "<ph_client_api_host>")
config.sessionReplay = true
config.sessionReplayConfig.captureLogs = true // TIP: Needs to be configured in code. Project settings won't have an effect.

PostHogSDK.shared.setup(config)
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Important:`}</strong>{` Individual console logs are truncated at 2000 characters. The rest of the log is not sent to PostHog. When truncating a log message we add ...`}{`[Truncated]`}{` to the message.`}</p>
    </blockquote>
    <h3 {...{
      "id": "sanitizing-or-skipping-logs"
    }}>{`Sanitizing or skipping logs`}</h3>
    <p>{`PostHog iOS SDK allows you to customize how log messages are processed and categorized by providing a custom `}<inlineCode parentName="p">{`logSanitizer`}</inlineCode>{` handler. `}</p>
    <p>{`This handler is called for each new line printed to the console. It receives the line's content and can return either a processed log entry with its severity level, or `}<inlineCode parentName="p">{`nil`}</inlineCode>{` to skip the log entirely:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ios_swift"
      }}>{`let config = PostHogConfig(apiKey: "<ph_project_api_key>", host: "<ph_client_api_host>")

// Custom log processing and sanitization
config.sessionReplayConfig.captureLogsConfig.logSanitizer = { output in

    // Skip trace-level logs
    guard !output.contains("TRACE") else {
        return nil
    }

    // Determine log level based on message content
    let level: PostHogLogLevel = {
        if output.contains("CRITICAL") { return .error }
        if output.contains("WARN") { return .warn }
        return .info
    }()
    
    // Remove sensitive information (e.g., auth tokens)
    let sanitizedMessage = output.replacingOccurrences(
        of: #"Bearer\\s+[A-Za-z0-9-._~+/]+=*"#,
        with: "Bearer [TOKEN REDACTED]",
        options: .regularExpression
    )
    
    return PostHogLogEntry(level: level, message: sanitizedMessage)
}

PostHogSDK.shared.setup(config)
`}</code></pre>
    <h3 {...{
      "id": "configuring-minimum-log-level"
    }}>{`Configuring minimum log level`}</h3>
    <p>{`By default, the SDK only captures error-level logs to minimize noise in session replays.
You can adjust this using the `}<inlineCode parentName="p">{`minLogLevel`}</inlineCode>{` setting:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ios_swift"
      }}>{`// Capture errors only (default)
config.sessionReplayConfig.captureLogsConfig.minLogLevel = .error

// Capture warnings and errors
config.sessionReplayConfig.captureLogsConfig.minLogLevel = .warn

// Capture all logs (most verbose)
config.sessionReplayConfig.captureLogsConfig.minLogLevel = .info
`}</code></pre>
    <p>{`Only logs with a level greater than or equal to the `}<inlineCode parentName="p">{`minLogLevel`}</inlineCode>{` will be captured. For example, if set to `}<inlineCode parentName="p">{`.warn`}</inlineCode>{`, both `}<strong parentName="p">{`warning`}</strong>{` and `}<strong parentName="p">{`error`}</strong>{` logs will be captured, but `}<strong parentName="p">{`info`}</strong>{` logs will be filtered out.`}</p>
    <h3 {...{
      "id": "default-behavior"
    }}>{`Default behavior`}</h3>
    <h4 {...{
      "id": "log-level"
    }}>{`Log level`}</h4>
    <p>{`The SDK captures raw console output directly, independent of any specific logging framework or implementation.
Since the raw output doesn't inherently carry log level information, the SDK deduces severity levels by analyzing the log content.
By default, the SDK automatically classifies log messages into three severity levels:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Error`}</strong>{` (highest severity)`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Messages containing: "error", "exception", "fail", "failed"`}</li>
          <li parentName="ul">{`OSLog entries with type "Error" or "Fault"`}</li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Warning`}</strong>{` (medium severity)`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Messages containing: "warning", "warn", "caution", "deprecated"`}</li>
          <li parentName="ul">{`OSLog entries with type "Warning"`}</li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Info`}</strong>{` (lowest severity)`}</p>
        <ul parentName="li">
          <li parentName="ul">{`All other messages that don't match error or warning patterns`}</li>
        </ul>
      </li>
    </ul>
    <h4 {...{
      "id": "filtering-and-processing"
    }}>{`Filtering and processing`}</h4>
    <ul>
      <li parentName="ul">{`Log filtering based on `}<inlineCode parentName="li">{`minLogLevel`}</inlineCode>{` (default: `}<inlineCode parentName="li">{`.error`}</inlineCode>{`). Only logs with equal or higher severity are captured. `}</li>
      <li parentName="ul">{`Multi-line logs are automatically split into separate entries`}</li>
      <li parentName="ul">{`OSLog metadata (e.g., subsystem, category) is automatically removed for cleaner output`}</li>
    </ul>
    <p>{`The SDK captures logs from:`}</p>
    <ul>
      <li parentName="ul">{`Standard output (stdout)`}</li>
      <li parentName="ul">{`Standard error (stderr)`}</li>
      <li parentName="ul">{`OSLog messages`}</li>
      <li parentName="ul">{`NSLog messages`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      